import type {GroupCallState, ParticipantView} from '@phonehq/rtc-core';
import type {MatrixCallSessionState} from '../../../services/matrixCalling/CallSessionState';
import {CallStateEnum} from '../../../components/Dialer/callState';
import {getFormatedPhoneNumber} from '../../../utilis/msisdnUtilis';
import type {RtcFeatureFlags} from '../../groupCalls/services/rtcFeatureFlags';
import {resolveCallIdentity, type CallerSource} from './identity';
import type {
    CallCapability,
    CallLine,
    CallPeer,
    CallNetwork,
    GroupCallLeg,
    MatrixCallLeg,
    PhoneCallLeg,
} from './types';

export interface DialerCallSnapshot {
    readonly callState: CallStateEnum;
    readonly incomingCall: boolean;
    readonly callFrom: unknown;
    readonly callTo?: {readonly label?: string; readonly msisdn?: number | string; readonly msisdnId?: number};
    readonly selectedPhoneNumber?: unknown;
    readonly isSpam: string;
    readonly ivrKeyName?: string;
    readonly inCallSince?: number;
}

export interface MatrixCallControls {
    readonly muted: boolean;
    readonly held: boolean;
    readonly heldByPeer: boolean;
    readonly dtmf: boolean;
}

export interface IncomingGroupRing {
    readonly roomId: string;
    readonly notificationEventId: string;
    readonly intent: 'audio' | 'video';
    readonly callerUserId: string;
    readonly callerName: string;
    readonly roomName: string;
    readonly expiresAt: number;
}

const SIP_LEG_ID = 'sip:current';
const SPAM = 'true';
const WEAK_QUALITY = new Set(['poor', 'lost']);

const capabilities = (...items: (CallCapability | false)[]): ReadonlySet<CallCapability> =>
    new Set(items.filter((item): item is CallCapability => item !== false));

const asCaller = (value: unknown): CallerSource =>
    typeof value === 'string' || (value !== null && typeof value === 'object') ? (value as CallerSource) : undefined;

const lineOf = (value: unknown): CallLine | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const line = value as {label?: unknown; msisdn?: unknown; msisdnId?: unknown};
    if (line.msisdn === undefined || line.msisdn === null || line.msisdn === '') {
        return undefined;
    }
    return {
        label: typeof line.label === 'string' ? line.label : '',
        number: getFormatedPhoneNumber(String(line.msisdn).replace(/^\+/, ''), false),
        msisdnId: typeof line.msisdnId === 'number' ? line.msisdnId : undefined,
    };
};

export interface PhoneCallControls {
    readonly muted: boolean;
    readonly held: boolean;
    readonly direction?: 'incoming' | 'outgoing';
}

const NO_PHONE_CONTROLS: PhoneCallControls = {muted: false, held: false};

const dialNumberOf = (callFrom: unknown): string | undefined => {
    const raw = typeof callFrom === 'string'
        ? callFrom
        : callFrom && typeof callFrom === 'object'
            ? (callFrom as {msisdn?: unknown; phoneNumber?: unknown}).msisdn ?? (callFrom as {phoneNumber?: unknown}).phoneNumber
            : undefined;
    const digits = typeof raw === 'string' || typeof raw === 'number' ? String(raw).replace(/[^\d+]/g, '') : '';
    return digits.replace(/^\+/, '').length >= 3 ? `+${digits.replace(/^\+/, '')}` : undefined;
};

export const phoneLegOf = (
    dialer: DialerCallSnapshot,
    controls: PhoneCallControls = NO_PHONE_CONTROLS,
): PhoneCallLeg | undefined => {
    const {callState, incomingCall} = dialer;
    const direction = incomingCall || controls.direction === 'incoming' ? 'incoming' : 'outgoing';
    const phase =
        callState === CallStateEnum.IN_CALL
            ? 'active'
            : callState === CallStateEnum.CONNECTING
              ? direction === 'incoming' ? 'active' : 'calling'
              : callState === CallStateEnum.IDLE && incomingCall
                ? 'incoming'
                : undefined;
    if (!phase) {
        return undefined;
    }
    return {
        id: SIP_LEG_ID,
        engine: 'sip',
        kind: 'phone',
        phase,
        direction,
        identity: resolveCallIdentity(asCaller(dialer.callFrom)),
        connectedAt: dialer.inCallSince,
        muted: controls.muted,
        held: controls.held,
        network: 'ok',
        dialNumber: dialNumberOf(dialer.callFrom),
        line: lineOf(direction === 'incoming' ? dialer.callTo ?? dialer.selectedPhoneNumber : dialer.selectedPhoneNumber),
        likelySpam: dialer.isSpam === SPAM,
        ivrKey: dialer.ivrKeyName || undefined,
        capabilities: capabilities('mute', 'hold', 'keypad', 'speaker', 'devices'),
    };
};

const peerOf = (callFrom: unknown): CallPeer | undefined => {
    if (!callFrom || typeof callFrom !== 'object') {
        return undefined;
    }
    const record = callFrom as {userId?: unknown; firstName?: unknown; lastName?: unknown};
    if (typeof record.userId !== 'string' && typeof record.userId !== 'number') {
        return undefined;
    }
    return {
        userId: record.userId,
        firstName: typeof record.firstName === 'string' ? record.firstName : undefined,
        lastName: typeof record.lastName === 'string' ? record.lastName : undefined,
    };
};

const isActiveMatrixState = (
    state: MatrixCallSessionState,
): state is Extract<MatrixCallSessionState, {kind: 'ringing' | 'connecting' | 'connected' | 'reconnecting'}> =>
    state.kind === 'ringing' || state.kind === 'connecting' || state.kind === 'connected' || state.kind === 'reconnecting';

export const matrixLegOf = (
    state: MatrixCallSessionState,
    callFrom: unknown,
    controls: MatrixCallControls,
    flags: RtcFeatureFlags,
): MatrixCallLeg | undefined => {
    if (!isActiveMatrixState(state)) {
        return undefined;
    }
    const incoming = state.direction === 'incoming';
    const phase = state.kind === 'ringing' ? (incoming ? 'incoming' : 'calling') : state.kind === 'connecting' && !incoming ? 'calling' : 'active';
    const media = state.media;
    const video = media?.intent === 'video' || !!media?.localVideo || !!media?.remoteVideo;
    const caller = asCaller(callFrom);
    const identity = resolveCallIdentity(caller);
    return {
        id: `matrix:${state.call.matrixCallId}`,
        engine: 'matrix',
        kind: video ? 'video' : 'voice',
        phase,
        direction: state.direction,
        identity: {...identity, userId: identity.userId ?? state.call.remoteUserId},
        connectedAt: 'connectedAtMs' in state ? state.connectedAtMs : undefined,
        muted: controls.muted,
        held: controls.held,
        heldByPeer: controls.heldByPeer,
        peer: peerOf(callFrom),
        network: state.kind === 'reconnecting' ? 'reconnecting' : 'ok',
        roomId: state.call.roomId,
        matrixCallId: state.call.matrixCallId,
        media: {
            localVideo: !!media?.localVideo,
            remoteVideo: !!media?.remoteVideo,
            localScreenShare: !!media?.localScreenShare,
            remoteScreenShare: !!media?.remoteScreenShare,
        },
        capabilities: capabilities(
            'mute',
            'hold',
            controls.dtmf && 'keypad',
            'speaker',
            'devices',
            flags.videoCalls && 'camera',
            flags.screenShare && 'share',
            flags.groupCalls && 'addPeople',
        ),
    };
};

const groupNetwork = (state: GroupCallState): CallNetwork => {
    if (state.phase === 'reconnecting') {
        return 'reconnecting';
    }
    const local = state.participants.find(participant => participant.isLocal);
    return local && WEAK_QUALITY.has(local.connectionQuality) ? 'weak' : 'ok';
};

const remoteParticipants = (participants: readonly ParticipantView[]): readonly ParticipantView[] =>
    participants.filter(participant => !participant.isLocal);

const uniqueUsers = (participants: readonly ParticipantView[]): number =>
    new Set(participants.map(participant => participant.userId)).size;

const GROUP_LIVE_PHASES = new Set<GroupCallState['phase']>(['joining', 'joined', 'reconnecting', 'leaving']);

export const groupLegOf = (
    state: GroupCallState,
    roomName: string | undefined,
    flags: RtcFeatureFlags,
    sessionStartedAt?: number,
): GroupCallLeg | undefined => {
    if (!state.roomId || !GROUP_LIVE_PHASES.has(state.phase)) {
        return undefined;
    }
    return {
        id: `group:${state.roomId}`,
        engine: 'group',
        kind: 'group',
        phase: state.phase === 'joining' ? 'calling' : 'active',
        direction: 'outgoing',
        identity: {name: roomName},
        connectedAt: state.joinedAt,
        muted: !state.local.microphoneEnabled,
        held: false,
        network: groupNetwork(state),
        roomId: state.roomId,
        roomName,
        intent: state.intent,
        participants: remoteParticipants(state.participants),
        participantCount: Math.max(1, uniqueUsers(state.participants)),
        encryption: state.encryption.status,
        local: state.local,
        full: state.full,
        minimized: state.minimized,
        sessionStartedAt,
        capabilities: capabilities(
            'mute',
            'people',
            'devices',
            flags.videoCalls && 'camera',
            flags.screenShare && 'share',
            'addPeople',
        ),
    };
};

export const incomingGroupLegOf = (ring: IncomingGroupRing): GroupCallLeg => ({
    id: `group-ring:${ring.notificationEventId}`,
    engine: 'group',
    kind: 'group',
    phase: 'incoming',
    direction: 'incoming',
    identity: {name: ring.callerName, userId: ring.callerUserId},
    muted: false,
    held: false,
    network: 'ok',
    roomId: ring.roomId,
    roomName: ring.roomName,
    intent: ring.intent,
    participants: [],
    participantCount: 0,
    encryption: 'ok',
    local: {microphoneEnabled: true, cameraEnabled: ring.intent === 'video', screenSharing: false},
    full: false,
    minimized: true,
    notificationEventId: ring.notificationEventId,
    expiresAt: ring.expiresAt,
    capabilities: capabilities(),
});
